import { styled } from '@abyss/web/tools/styled';
import { Flex } from '@abyss/web/ui/Flex';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { RatingText } from '../../../../../common/DataCard/DataCard.style';
import { PrintViewDivider } from './PrintView.style';
import {
  getAddress,
  getCostIndicatorDescription,
  getHoursOfOperation,
  getObjectStringParsed,
  getPhoneNumbers,
} from './printViewUtils';
import { getStarsRating } from './utils';

const baseBoldTextStyle = {
  fontWeight: '700',
  color: '#212223',
};

export const PrintViewBoldText = styled('span', {
  ...baseBoldTextStyle,
});

export const PrintViewBoldTextWithMarginRight = styled('span', {
  ...baseBoldTextStyle,
  marginRight: '2px',
});

const BehavioralHealthProviderPrintView = ({ portalData, provider }) => {
  const { t } = useTranslation();
  const notAvailable = t('PROVIDER_LOCATIONS_DETAILS.NOT_AVAILABLE_UNKNOWN');

  const acceptingNewPatients = provider?.acceptingNewPatients;
  const accessibilities = provider?.accessibility?.join(', ') || notAvailable;
  const additionalAddresses = [];
  const address = getAddress(provider?.address) || notAvailable;
  const boardCertifications =
    provider?.boardCertifications?.join(', ') || notAvailable;
  const constIndicator = getCostIndicatorDescription(
    provider?.costIndicator,
    t
  );
  const culturalCompetencies =
    provider?.culturalCompetencies?.join(', ') || notAvailable;
  const email = provider?.emailAddress?.join(', ') || notAvailable;
  const faxPhone = getPhoneNumbers(provider?.phones?.fax) || notAvailable;
  const gender = provider?.gender;
  const hospitalAffiliations =
    getObjectStringParsed(provider?.hospitalAffiliations) || notAvailable;
  const hoursOfOperation = getHoursOfOperation([provider?.hoursOfOperation], t);
  const isPremiumCarePhysician = provider?.isPremiumCarePhysician === 'YES';
  const isTieredProvider = provider?.isTieredProvider;
  const languagesSpoken = provider?.languagesSpoken?.join(', ') || notAvailable;
  const languagesSpokenByInterpreters =
    provider?.languagesSpokenByInterpreters?.join(', ') || notAvailable;
  const languagesSpokenByOffice =
    provider?.languagesSpokenByOffice?.join(', ') || notAvailable;
  const licenseNumber = provider?.licenseNumber?.join(', ') || notAvailable;
  const networkOrPlanName = portalData?.planName || notAvailable;
  const npi = provider?.npi || notAvailable;
  const practiceAgeLimitations =
    provider?.practiceLimitations?.age || notAvailable;
  const practiceGenderLimitations =
    provider?.practiceLimitations?.gender || notAvailable;
  const providerGroupsAffiliations = getObjectStringParsed(
    provider?.providerGroupsAffiliations,
    t
  );
  const providerName = provider?.providerName || notAvailable;

  const rating = provider?.healthGradeRating;
  const parsedProviderRating = parseFloat(parseFloat(rating).toFixed(1));
  const specialties = getObjectStringParsed(provider?.specialities, t);
  const telephone = getPhoneNumbers(provider?.phones?.phone) || notAvailable;
  const virtualCareOffered =
    provider?.virtualCareOffered?.length > 0 ? 'Yes' : notAvailable;
  const website = provider?.website || notAvailable;

  return (
    <React.Fragment>
      {/* index and name section start */}
      <Flex
        css={{
          'abyss-flex-root': {
            gap: '2px',
            padding: 0,
            flexWrap: 'nowrap',
            width: '100%',
            fontSize: '10px',
          },
        }}
        direction="column"
      >
        <PrintViewBoldText>
          {providerName} {gender ? `(${gender})` : ''}
        </PrintViewBoldText>
      </Flex>
      {/* index and name section end */}

      {/* Specialties section start */}
      <Flex
        css={{
          'abyss-flex-root': {
            gap: '2px',
            padding: 0,
            flexWrap: 'nowrap',
            width: '100%',
            fontSize: '10px',
          },
        }}
        direction="row"
      >
        <PrintViewBoldTextWithMarginRight>
          {t('PROVIDER_LOCATIONS_DETAILS.SPECIALTIES')}:
        </PrintViewBoldTextWithMarginRight>
        <span>{specialties}</span>
      </Flex>
      {/* Specialties section end */}

      {/* rating, accepting new patients, virtual care section end */}
      <Flex
        css={{
          'abyss-flex-root': {
            gap: '2px',
            padding: 0,
            flexWrap: 'nowrap',
            width: '100%',
            fontSize: '10px',
            marginTop: '20px',
            marginBottom: '20px',
          },
        }}
        justify="flex-start"
      >
        {rating && (
          <Flex
            css={{
              'abyss-flex-root': {
                marginRight: '5px',
                gap: '4px',
              },
            }}
            direction="row"
          >
            {getStarsRating(parsedProviderRating)}
            {parsedProviderRating ? (
              <RatingText className="text" size="11px">
                {provider?.numberOfReviews
                  ? `${provider?.numberOfReviews} Reviews`
                  : parsedProviderRating}
              </RatingText>
            ) : null}
          </Flex>
        )}
        {isPremiumCarePhysician && (
          <Flex
            css={{
              'abyss-flex-root': {
                marginRight: '8px',
                gap: '4px',
              },
            }}
            direction="row"
          >
            <PrintViewBoldText>
              <IconMaterial
                color="#111"
                css={{
                  width: '12px',
                  height: '12px',
                  marginRight: '0px',
                  marginBottom: '-2px',
                }}
                icon="favorite"
              />
              <IconMaterial
                color="#111"
                css={{
                  width: '12px',
                  height: '12px',
                  marginRight: '0px',
                  marginBottom: '-2px',
                }}
                icon="favorite"
              />{' '}
              {t('SHARE_PDF.Premium Care Physician')}
            </PrintViewBoldText>
          </Flex>
        )}
        {isTieredProvider && (
          <Flex
            css={{
              'abyss-flex-root': {
                marginRight: '5px',
                gap: '4px',
              },
            }}
            direction="row"
          >
            <PrintViewBoldText>
              <IconMaterial
                color="#111"
                css={{
                  width: '12px',
                  height: '12px',
                  marginRight: '0px',
                  marginBottom: '-2px',
                }}
                icon="check"
              />{' '}
              {t('SHARE_PDF.Tier 1 Provider')}
            </PrintViewBoldText>
          </Flex>
        )}
        {acceptingNewPatients && (
          <Flex
            css={{
              'abyss-flex-root': {
                marginRight: '8px',
                gap: '4px',
              },
            }}
            direction="row"
          >
            <PrintViewBoldText>
              <IconMaterial
                color="#111"
                css={{
                  width: '12px',
                  height: '12px',
                  marginRight: '0px',
                  marginBottom: '-2px',
                }}
                icon="check"
              />{' '}
              {t('SHARE_PDF.Accepting new patients')}
            </PrintViewBoldText>
          </Flex>
        )}
        {constIndicator && (
          <Flex
            css={{
              'abyss-flex-root': {
                marginRight: '5px',
                gap: '4px',
              },
            }}
            direction="row"
          >
            <PrintViewBoldText>
              {provider?.costIndicator} - {constIndicator}
            </PrintViewBoldText>
          </Flex>
        )}
      </Flex>
      {/* rating, accepting new patients, virtual care section end */}

      {/* address section start */}
      <Flex
        css={{
          'abyss-flex-root': {
            gap: '2px',
            padding: 0,
            flexWrap: 'nowrap',
            width: '100%',
            fontSize: '10px',
          },
        }}
        direction="row"
      >
        <PrintViewBoldTextWithMarginRight>
          {t('PROVIDER_LOCATIONS_DETAILS.ADDRESS')}:
        </PrintViewBoldTextWithMarginRight>
        <span>{address}</span>
      </Flex>
      {/* address section end */}

      {/* primary phone, fax, email section end */}
      <Flex
        css={{
          'abyss-flex-root': {
            gap: '2px',
            padding: 0,
            flexWrap: 'nowrap',
            width: '100%',
            fontSize: '10px',
          },
        }}
        justify="flex-start"
      >
        <Flex
          css={{
            'abyss-flex-root': {
              marginRight: '5px',
              gap: '4px',
            },
          }}
          direction="row"
        >
          <PrintViewBoldTextWithMarginRight>
            {t('PROVIDER_LOCATIONS_DETAILS.PRIMARY_PHONE')}:
          </PrintViewBoldTextWithMarginRight>
          <span>{telephone}</span>
        </Flex>

        <Flex
          css={{
            'abyss-flex-root': {
              marginRight: '8px',
              gap: '4px',
            },
          }}
          direction="row"
        >
          <PrintViewBoldTextWithMarginRight>
            {t('SHARE_PDF.Fax')}:
          </PrintViewBoldTextWithMarginRight>
          <span>{faxPhone}</span>
        </Flex>

        <Flex
          css={{
            'abyss-flex-root': {
              marginRight: '5px',
              gap: '4px',
            },
          }}
          direction="row"
        >
          <Flex direction="row">
            <PrintViewBoldTextWithMarginRight>
              {t('FEEDBACK_FORM.EMAIL')}:
            </PrintViewBoldTextWithMarginRight>
            <span>{email}</span>
          </Flex>
        </Flex>
      </Flex>
      {/* primary phone, fax, email section end */}

      {/* Website section start */}
      <Flex
        css={{
          'abyss-flex-root': {
            gap: '2px',
            padding: 0,
            flexWrap: 'nowrap',
            width: '100%',
            fontSize: '10px',
          },
        }}
        direction="row"
      >
        <PrintViewBoldTextWithMarginRight>
          {t('Website')}:
        </PrintViewBoldTextWithMarginRight>
        <span>{website}</span>
      </Flex>
      {/* Website section end */}

      {/* Office hours section start */}
      <Flex
        css={{
          'abyss-flex-root': {
            gap: '2px',
            padding: 0,
            flexWrap: 'nowrap',
            width: '100%',
            fontSize: '10px',
          },
        }}
        direction="row"
      >
        <PrintViewBoldTextWithMarginRight>
          {t('SHARE_PDF.Office hours')}:
        </PrintViewBoldTextWithMarginRight>
        <span>
          {hoursOfOperation?.map((item, index) => (
            <Flex direction="row" key={index}>
              <PrintViewBoldTextWithMarginRight>
                {item.day}:
              </PrintViewBoldTextWithMarginRight>
              <span>{item.timing}</span>
            </Flex>
          ))}
        </span>
      </Flex>
      {/* Office hours section end */}

      {/* Virtual care section start */}
      <Flex
        css={{
          'abyss-flex-root': {
            gap: '2px',
            padding: 0,
            flexWrap: 'nowrap',
            width: '100%',
            fontSize: '10px',
          },
        }}
        direction="row"
      >
        <PrintViewBoldTextWithMarginRight>
          {t('PROVIDER_LOCATIONS_DETAILS.OFFERS_VIRTUAL_CARE')}:
        </PrintViewBoldTextWithMarginRight>
        <span>{virtualCareOffered}</span>
      </Flex>
      {/* Virtual care section end */}

      {/* Board certificate section start */}
      <Flex
        css={{
          'abyss-flex-root': {
            gap: '2px',
            padding: 0,
            flexWrap: 'nowrap',
            width: '100%',
            fontSize: '10px',
            marginTop: '10px',
          },
        }}
        direction="row"
      >
        <PrintViewBoldTextWithMarginRight>
          {t('SMART_CHOICE.PREFERENCES.Board_certified')}:
        </PrintViewBoldTextWithMarginRight>
        <span>{boardCertifications}</span>
      </Flex>
      {/* Board certificate section end */}

      {/* Provider group section start */}
      <Flex
        css={{
          'abyss-flex-root': {
            gap: '2px',
            padding: 0,
            flexWrap: 'nowrap',
            width: '100%',
            fontSize: '10px',
          },
        }}
        direction="row"
      >
        <PrintViewBoldTextWithMarginRight>
          {t('Provider group')}:
        </PrintViewBoldTextWithMarginRight>
        <span>{providerGroupsAffiliations}</span>
      </Flex>
      {/* Provider group section end */}

      {/* hospital Affiliations section start */}
      <Flex
        css={{
          'abyss-flex-root': {
            gap: '2px',
            padding: 0,
            flexWrap: 'nowrap',
            width: '100%',
            fontSize: '10px',
          },
        }}
        direction="row"
      >
        <PrintViewBoldTextWithMarginRight>
          {t(
            'PROVIDER_DETAILS.ABOUT_TAB.DETAILS_SECTION.HOSPITAL_AFFILIATIONS'
          )}
          :
        </PrintViewBoldTextWithMarginRight>
        <span>{hospitalAffiliations}</span>
      </Flex>
      {/* hospital Affiliations section end */}

      {/* Network and plan name section start */}
      <Flex
        css={{
          'abyss-flex-root': {
            gap: '2px',
            padding: 0,
            flexWrap: 'nowrap',
            width: '100%',
            fontSize: '10px',
            marginTop: '10px',
          },
        }}
        direction="row"
      >
        <PrintViewBoldTextWithMarginRight>
          {t('SHARE_PDF.Network/Plan name')}:
        </PrintViewBoldTextWithMarginRight>
        <span>{networkOrPlanName}</span>
      </Flex>
      {/* Network and plan name section end */}

      {/*License Number and License type section start */}
      <Flex
        css={{
          'abyss-flex-root': {
            gap: '2px',
            padding: 0,
            flexWrap: 'nowrap',
            width: '100%',
            fontSize: '10px',
          },
        }}
        justify="flex-start"
      >
        <Flex
          css={{
            'abyss-flex-root': {
              marginRight: '5px',
              gap: '4px',
            },
          }}
          direction="row"
        >
          <PrintViewBoldTextWithMarginRight>
            {t('SHARE_PDF.License No')}:
          </PrintViewBoldTextWithMarginRight>
          <span>{licenseNumber}</span>
        </Flex>
      </Flex>
      {/*License Number and License type section end */}

      {/* NPI section start */}
      <Flex
        css={{
          'abyss-flex-root': {
            gap: '2px',
            padding: 0,
            flexWrap: 'nowrap',
            width: '100%',
            fontSize: '10px',
          },
        }}
        direction="row"
      >
        <PrintViewBoldText>
          {t('SHARE_PDF.National provider No. (NPI)')}:
        </PrintViewBoldText>
        <span>{npi}</span>
      </Flex>
      {/* NPI section end */}

      {/* accessibility section start */}
      <Flex
        css={{
          'abyss-flex-root': {
            gap: '2px',
            padding: 0,
            flexWrap: 'nowrap',
            width: '100%',
            fontSize: '10px',
            marginTop: '10px',
          },
        }}
        direction="row"
      >
        <PrintViewBoldTextWithMarginRight>
          {t('PROVIDER_LOCATIONS_DETAILS.ACCESSIBILITY')}:
        </PrintViewBoldTextWithMarginRight>
        <span>{accessibilities}</span>
      </Flex>
      {/* accessibility section end */}

      {/* Language spoken by provider section start */}
      <Flex
        css={{
          'abyss-flex-root': {
            gap: '2px',
            padding: 0,
            flexWrap: 'nowrap',
            width: '100%',
            fontSize: '10px',
          },
        }}
        direction="row"
      >
        <PrintViewBoldTextWithMarginRight>
          {t('PROVIDER_LOCATIONS_DETAILS.LANGUAGES_SPOKEN_BY_PROVIDER')}:
        </PrintViewBoldTextWithMarginRight>
        <span>{languagesSpoken}</span>
      </Flex>
      {/* Language spoken by provider  section end */}

      {/* Language spoken by interpreter section start */}
      <Flex
        css={{
          'abyss-flex-root': {
            gap: '2px',
            padding: 0,
            flexWrap: 'nowrap',
            width: '100%',
            fontSize: '10px',
          },
        }}
        direction="row"
      >
        <PrintViewBoldTextWithMarginRight>
          {t('PROVIDER_LOCATIONS_DETAILS.LANGUAGES_SPOKEN_BY_INTERPRETER')}:
        </PrintViewBoldTextWithMarginRight>
        <span>{languagesSpokenByInterpreters}</span>
      </Flex>
      {/* Language spoken by interpreter  section end */}

      {/* Language spoken by office section start */}
      <Flex
        css={{
          'abyss-flex-root': {
            gap: '2px',
            padding: 0,
            flexWrap: 'nowrap',
            width: '100%',
            fontSize: '10px',
          },
        }}
        direction="row"
      >
        <PrintViewBoldTextWithMarginRight>
          {t('PROVIDER_LOCATIONS_DETAILS.LANGUAGES_SPOKEN_BY_OFFICE')}:
        </PrintViewBoldTextWithMarginRight>
        <span>{languagesSpokenByOffice}</span>
      </Flex>
      {/* Language spoken by office  section end */}

      {/* Practice gender section start */}
      <Flex
        css={{
          'abyss-flex-root': {
            gap: '2px',
            padding: 0,
            flexWrap: 'nowrap',
            width: '100%',
            fontSize: '10px',
          },
        }}
        direction="row"
      >
        <PrintViewBoldTextWithMarginRight>
          {t('SHARE_PDF.Practice gender limitations')}:
        </PrintViewBoldTextWithMarginRight>
        <span>{practiceGenderLimitations}</span>
      </Flex>
      {/* practice gender  section end */}

      {/* practice age section start */}
      <Flex
        css={{
          'abyss-flex-root': {
            gap: '2px',
            padding: 0,
            flexWrap: 'nowrap',
            width: '100%',
            fontSize: '10px',
          },
        }}
        direction="row"
      >
        <PrintViewBoldTextWithMarginRight>
          {t('SHARE_PDF.Practice age limitations')}:
        </PrintViewBoldTextWithMarginRight>
        <span>{practiceAgeLimitations}</span>
      </Flex>
      {/* practice age  section end */}

      {/* Cultural Competencies section start */}
      <Flex
        css={{
          'abyss-flex-root': {
            gap: '2px',
            padding: 0,
            flexWrap: 'nowrap',
            width: '100%',
            fontSize: '10px',
          },
        }}
        direction="row"
      >
        <PrintViewBoldTextWithMarginRight>
          {t('PROVIDER_GROUP_DETAILS.ABOUT_TAB.CULTURAL_COMPETENCE')}:
        </PrintViewBoldTextWithMarginRight>
        <span>{culturalCompetencies}</span>
      </Flex>
      {/* Cultural Competencies  section end */}

      {/* Provider locations available in the following location start */}
      {additionalAddresses.map((address, index) => (
        <Flex
          css={{
            'abyss-flex-root': {
              gap: '2px',
              padding: 0,
              flexWrap: 'nowrap',
              width: '100%',
              fontSize: '10px',
              marginTop: '10px',
            },
          }}
          direction="row"
          key={index}
        >
          <PrintViewBoldTextWithMarginRight>
            {t('SHARE_PDF.This provider is also available at')}:
          </PrintViewBoldTextWithMarginRight>
          <span>{getAddress(address)}</span>
        </Flex>
      ))}
      {/* Provider locations available in the following location end */}

      <PrintViewDivider orientation="horizontal" />
    </React.Fragment>
  );
};

export default BehavioralHealthProviderPrintView;
